import { readFileSync, writeFileSync, mkdirSync, existsSync } from "node:fs";
import { resolve, dirname } from "node:path";
import type {
  SyncConfig,
  SyncProvider,
  SyncItem,
  SyncResult,
  SyncManager,
  SyncedItem,
  SyncLedger,
  StatusUpdate,
} from "./types.js";
import { createLinearProvider } from "./providers/linear.js";
import { createGitHubProvider } from "./providers/github.js";
import { createNotionProvider } from "./providers/notion.js";
import { createJiraProvider } from "./providers/jira.js";

const LEDGER_DIR = ".fathom";
const LEDGER_FILE = "sync-ledger.json";

function resolveApiKey(config: SyncConfig): string {
  if (config.apiKey) return config.apiKey;
  const envVar = config.apiKeyEnv ?? getDefaultEnvVar(config.provider);
  const key = process.env[envVar];
  if (!key) {
    throw new Error(
      `API key not found. Set ${envVar} environment variable or provide apiKey in config.`,
    );
  }
  return key;
}

function getDefaultEnvVar(provider: string): string {
  switch (provider) {
    case "linear":
      return "LINEAR_API_KEY";
    case "github":
      return "GITHUB_TOKEN";
    case "notion":
      return "NOTION_API_KEY";
    case "jira":
      return "JIRA_API_TOKEN";
    default:
      return `${provider.toUpperCase()}_API_KEY`;
  }
}

function createProvider(config: SyncConfig, apiKey: string): SyncProvider {
  switch (config.provider) {
    case "linear":
      return createLinearProvider(apiKey, config.projectKey, config.statusMapping);
    case "github":
      return createGitHubProvider(apiKey, config.projectKey, config.statusMapping);
    case "notion":
      return createNotionProvider(apiKey, config.projectKey, config.statusMapping);
    case "jira":
      return createJiraProvider(apiKey, config.projectKey, config.statusMapping);
    default:
      throw new Error(`Unsupported provider: ${config.provider}`);
  }
}

function getLedgerPath(basePath?: string): string {
  const base = basePath ?? process.cwd();
  return resolve(base, LEDGER_DIR, LEDGER_FILE);
}

function loadLedger(ledgerPath: string): SyncLedger {
  if (existsSync(ledgerPath)) {
    try {
      return JSON.parse(readFileSync(ledgerPath, "utf-8"));
    } catch {
      // corrupt ledger, start fresh
    }
  }
  return { provider: "", projectKey: "", items: [], lastSync: 0 };
}

function saveLedger(ledgerPath: string, ledger: SyncLedger): void {
  mkdirSync(dirname(ledgerPath), { recursive: true });
  writeFileSync(ledgerPath, JSON.stringify(ledger, null, 2) + "\n");
}

export function createSyncManager(
  config: SyncConfig,
  options?: { basePath?: string },
): SyncManager {
  const apiKey = resolveApiKey(config);
  const provider = createProvider(config, apiKey);
  const ledgerPath = getLedgerPath(options?.basePath);

  return {
    async syncSlices(slices: SyncItem[]): Promise<SyncResult[]> {
      const ledger = loadLedger(ledgerPath);
      const syncedIds = new Set(ledger.items.map((i) => i.sliceId));
      const results: SyncResult[] = [];

      for (const slice of slices) {
        // Skip already-synced slices
        if (syncedIds.has(slice.sliceId)) continue;

        const result = await provider.createItem(slice);
        results.push(result);

        ledger.items.push({
          sliceId: slice.sliceId,
          externalId: result.externalId,
          url: result.url,
          status: "backlog",
          lastSynced: Date.now(),
        });
      }

      ledger.provider = config.provider;
      ledger.projectKey = config.projectKey;
      ledger.lastSync = Date.now();
      saveLedger(ledgerPath, ledger);

      return results;
    },

    async pullStatus(): Promise<StatusUpdate[]> {
      const ledger = loadLedger(ledgerPath);
      const updates: StatusUpdate[] = [];

      for (const item of ledger.items) {
        const currentStatus = await provider.getStatus(item.externalId);
        if (currentStatus !== item.status) {
          updates.push({
            sliceId: item.sliceId,
            externalId: item.externalId,
            previousStatus: item.status,
            newStatus: currentStatus,
          });
          item.status = currentStatus;
          item.lastSynced = Date.now();
        }
      }

      if (updates.length > 0) {
        ledger.lastSync = Date.now();
        saveLedger(ledgerPath, ledger);
      }

      return updates;
    },

    async getLedger(): Promise<SyncedItem[]> {
      const ledger = loadLedger(ledgerPath);
      return ledger.items;
    },
  };
}
